import { ethers } from 'ethers';
import transactionService from './transactionService';

/**
 * Service for interacting with the Innovation Units contract
 */
class InnovationUnitsService {
  /**
   * Create a new InnovationUnitsService instance
   * @param {Object} contract - Ethers.js contract instance
   * @param {Object} celTokenContract - CEL Token contract instance
   */
  constructor(contract, celTokenContract) {
    this.contract = contract;
    this.celToken = celTokenContract;
  }

  /**
   * Get the balance of Innovation Units for a given address and project
   * @param {string} address - Address to check
   * @param {number} projectId - Project ID
   * @returns {Promise<BigNumber>} - Balance of Innovation Units
   */
  async balanceOf(address, projectId) {
    try {
      const balance = await this.contract.balanceOf(address, projectId);
      return balance;
    } catch (error) {
      console.error('Error getting Innovation Units balance:', error);
      throw error;
    }
  }

  /**
   * Get the total number of projects
   * @returns {Promise<number>} - Total number of projects
   */
  async getTotalProjects() {
    try {
      const totalProjects = await this.contract.getTotalProjects();
      return totalProjects.toNumber();
    } catch (error) {
      console.error('Error getting total projects:', error);
      throw error;
    }
  }

  /**
   * Check if a project exists
   * @param {number} projectId - Project ID
   * @returns {Promise<boolean>} - True if project exists
   */
  async projectExists(projectId) {
    try {
      return await this.contract.projectIdExists(projectId);
    } catch (error) {
      console.error('Error checking if project exists:', error);
      throw error;
    }
  }

  /**
   * Get project data
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Project data
   */
  async getProjectData(projectId) {
    try {
      const projectData = await this.contract.getProjectData(projectId);
      return {
        totalSupply: projectData[0],
        initialPrice: projectData[1],
        creatorsAllocatedPercentage: projectData[2],
        contributorsReservePercentage: projectData[3],
        investorsReservePercentage: projectData[4],
        treasuryBalance: projectData[5],
        name: projectData[6]
      };
    } catch (error) {
      console.error('Error getting project data:', error);
      throw error;
    }
  }

  /**
   * Get project name
   * @param {number} projectId - Project ID
   * @returns {Promise<string>} - Project name
   */
  async getProjectName(projectId) {
    try {
      const name = await this.contract.getProjectName(projectId);
      return name;
    } catch (error) {
      console.error('Error getting project name:', error);
      throw error;
    }
  }

  /**
   * Create a new project
   
   * @param {BigNumber} initialPrice - Initial price per Innovation Unit
   * @param {string[]} creators - Array of creator addresses
   * @param {number[]} creatorShares - Array of creator shares (percentage * 100)
   * @param {number} creatorAllocation - Percentage allocated to creators (percentage * 100)
   * @param {number} contributorAllocation - Percentage allocated to contributors (percentage * 100)
   * @param {number} investorAllocation - Percentage allocated to investors (percentage * 100)
   * @param {string} projectName - Name of the project
   * @returns {Promise<Object>} - Transaction result with project ID
   */
  async createProject(
    
    initialPrice,
    creators,
    creatorShares,
    creatorAllocation,
    contributorAllocation,
    investorAllocation,
    projectName
  ) {
    try {
      // Create project transaction
      const tx = this.contract.createProject(
        
        initialPrice,
        creators,
        creatorShares,
        creatorAllocation,
        contributorAllocation,
        investorAllocation,
        projectName
      );
      
      // Send transaction
      const result = await transactionService.sendTransaction(tx, {
        title: 'Create Project',
        description: 'Creating a new project on Celystik Hub',
        type: 'project_creation'
      });
      
      // Extract project ID from events
      if (result.receipt && result.receipt.events) {
        const event = result.receipt.events.find(e => e.event === 'ProjectRegistered');
        if (event && event.args) {
          return {
            ...result,
            projectId: event.args.projectId.toNumber()
          };
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }

  /**
   * Mint Innovation Units to a contributor
   * @param {number} projectId - Project ID
   * @param {string} contributor - Contributor address
   * @param {number} amount - Amount of Innovation Units to mint
   * @returns {Promise<Object>} - Transaction result
   */
  async mintToContributor(projectId, contributor, amount) {
    try {
      const tx = this.contract.mintToContributor(projectId, contributor, amount);
      return await transactionService.sendTransaction(tx, {
        title: 'Mint to Contributor',
        description: `Minting ${amount} IUs to contributor ${contributor.substring(0, 6)}...`,
        type: 'mint_contributor'
      });
    } catch (error) {
      console.error('Error minting to contributor:', error);
      throw error;
    }
  }

  /**
   * Calculate the cost of buying Innovation Units
   * @param {number} projectId - Project ID
   * @param {number} amount - Amount of Innovation Units to buy
   * @returns {Promise<Object>} - Object containing basePayment, fee, and totalCost
   */
  async calculateBuyingCost(projectId, amount) {
    try {
      const result = await this.contract.calculateBuyingCost(projectId, amount);
      return {
        basePayment: result[0],
        fee: result[1],
        totalCost: result[2]
      };
    } catch (error) {
      console.error('Error calculating buying cost:', error);
      throw error;
    }
  }

  /**
   * Buy Innovation Units
   * @param {number} projectId - Project ID
   * @param {number} amount - Amount of Innovation Units to buy
   * @returns {Promise<Object>} - Transaction result
   */
  async buyIUs(projectId, amount) {
    console.log(`Starting buyIUs process for project ${projectId} with amount ${amount} IUs`);
    
    try {
      // Calculate cost
      const { totalCost, basePayment, fee } = await this.calculateBuyingCost(projectId, amount);
      console.log(`Cost calculation: Base payment: ${ethers.utils.formatEther(basePayment)} CEL, Fee: ${ethers.utils.formatEther(fee)} CEL, Total: ${ethers.utils.formatEther(totalCost)} CEL`);
      
      // Check if CEL token is approved
      const signer = this.contract.signer;
      const signerAddress = await signer.getAddress();
      console.log(`User wallet address: ${signerAddress}`);
      
      // First check if user has enough tokens
      const userBalance = await this.celToken.balanceOf(signerAddress);
      console.log(`User balance: ${ethers.utils.formatEther(userBalance)} CEL`);
      
      if (userBalance.lt(totalCost)) {
        throw new Error(`Insufficient token balance. You have ${ethers.utils.formatEther(userBalance)} CEL but trying to spend ${ethers.utils.formatEther(totalCost)} CEL.`);
      }
      
      // Get the contract address for token approval
      const contractAddress = this.contract.address;
      console.log(`InnovationUnits contract address for approval: ${contractAddress}`);
      
      // First, check the current allowance
      const currentAllowance = await this.celToken.allowance(signerAddress, contractAddress);
      console.log(`Current allowance: ${ethers.utils.formatEther(currentAllowance)} CEL`);
      
      // If allowance is insufficient, approve tokens
      if (currentAllowance.lt(totalCost)) {
        console.log(`Approving tokens: ${ethers.utils.formatEther(totalCost)} CEL for contract ${contractAddress}`);
        
        try {
          // Use the approve method directly
          const approvalTx = await this.celToken.approve(contractAddress, totalCost);
          console.log('Approval transaction submitted:', approvalTx);
          
          // Wait for approval transaction to be mined
          const approvalReceipt = await approvalTx.wait();
          console.log('Approval transaction receipt:', approvalReceipt);
          
          if (!approvalReceipt.status) {
            throw new Error('Token approval failed. Please try again.');
          }
          
          console.log('Token approval successful');
        } catch (approvalError) {
          console.error('Error during token approval:', approvalError);
          throw new Error(`Token approval failed: ${approvalError.message}`);
        }
      } else {
        console.log('Token allowance already sufficient for this transaction');
      }
      
      // Now execute the buy transaction
      console.log(`Executing buyIUs transaction for project ${projectId} with amount ${amount} IUs`);
      const tx = this.contract.buyIUs(projectId, amount);
      
      const result = await transactionService.sendTransaction(tx, {
        title: 'Buy IUs',
        description: `Buying ${amount} IUs for project ${projectId}`,
        type: 'buy_ius'
      });
      
      console.log('Transaction result:', result);
      
      // Format result for compatibility with UI expectations
      return {
        ...result,
        transactionHash: result.hash, // Add this for compatibility with submitInvestment
        success: result.status === 'confirmed'
      };
    } catch (error) {
      console.error('Error buying IUs:', error);
      
      // Provide more specific error messages
      if (error.message.includes("Exceeds investors allocation")) {
        throw new Error("This purchase exceeds the available Innovation Units for investors. Please try a smaller amount.");
      } else if (error.message.includes("ERC20: insufficient allowance")) {
        throw new Error("Token approval issue. Please try again and approve the transaction in your wallet.");
      } else if (error.message.includes("ERC20: transfer amount exceeds balance")) {
        throw new Error("Insufficient CEL tokens. Please make sure you have enough tokens to complete this transaction.");
      }
      
      throw error;
    }
  }

  /**
   * Calculate the return from selling Innovation Units
   * @param {number} projectId - Project ID
   * @param {number} amount - Amount of Innovation Units to sell
   * @returns {Promise<Object>} - Object containing baseReturn, fee, and netReturn
   */
  async calculateSellingReturn(projectId, amount) {
    try {
      const result = await this.contract.calculateSellingReturn(projectId, amount);
      return {
        baseReturn: result[0],
        fee: result[1],
        netReturn: result[2]
      };
    } catch (error) {
      console.error('Error calculating selling return:', error);
      throw error;
    }
  }

  /**
   * Sell Innovation Units
   * @param {number} projectId - Project ID
   * @param {number} amount - Amount of Innovation Units to sell
   * @returns {Promise<Object>} - Transaction result
   */
  async sellIUs(projectId, amount) {
    console.log(`Starting sellIUs process for project ${projectId} with amount ${amount} IUs`);
    
    try {
      // Calculate the expected return
      const { baseReturn, fee, netReturn } = await this.calculateSellingReturn(projectId, amount);
      console.log(`Return calculation: Base return: ${ethers.utils.formatEther(baseReturn)} CEL, Fee: ${ethers.utils.formatEther(fee)} CEL, Net return: ${ethers.utils.formatEther(netReturn)} CEL`);
      
      // Get the signer's address
      const signer = this.contract.signer;
      const signerAddress = await signer.getAddress();
      console.log(`User wallet address: ${signerAddress}`);
      
      // Check if the user has enough IUs to sell
      const userIUBalance = await this.balanceOf(signerAddress, projectId);
      console.log(`User IU balance: ${ethers.utils.formatUnits(userIUBalance, 0)} IUs`);
      
      if (userIUBalance.lt(amount)) {
        throw new Error(`Insufficient IU balance. You have ${ethers.utils.formatUnits(userIUBalance, 0)} IUs but trying to sell ${amount} IUs.`);
      }
      
      // Check if the project treasury has enough CEL tokens to pay the user
      const projectData = await this.getProjectData(projectId);
      console.log(`Project treasury balance: ${ethers.utils.formatEther(projectData.treasuryBalance)} CEL`);
      
      if (projectData.treasuryBalance.lt(baseReturn)) {
        throw new Error(`Insufficient project treasury balance. The project has ${ethers.utils.formatEther(projectData.treasuryBalance)} CEL but needs ${ethers.utils.formatEther(baseReturn)} CEL to complete this transaction.`);
      }
      
      // Execute the sell transaction
      console.log(`Executing sellIUs transaction for project ${projectId} with amount ${amount} IUs`);
      const tx = this.contract.sellIUs(projectId, amount);
      
      const result = await transactionService.sendTransaction(tx, {
        title: 'Sell IUs',
        description: `Selling ${amount} IUs from project ${projectId}`,
        type: 'sell_ius'
      });
      
      console.log('Transaction result:', result);
      
      // Format result for compatibility with UI expectations
      return {
        ...result,
        transactionHash: result.hash, // Add this for compatibility with UI
        success: result.status === 'confirmed',
        amountReceived: netReturn,
        feePaid: fee
      };
    } catch (error) {
      console.error('Error selling IUs:', error);
      
      // Provide more specific error messages
      if (error.message.includes("Insufficient balance")) {
        throw new Error("You don't have enough Innovation Units to complete this transaction.");
      } else if (error.message.includes("Insufficient project treasury balance")) {
        throw new Error("There are not enough CEL tokens in the project treasury to complete this redemption. Please try a smaller amount or contact the project owner.");
      }
      
      throw error;
    }
  }

  /**
   * Get the remaining contributor allocation for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<BigNumber>} - Remaining contributor allocation
   */
  async getRemainingContributorAllocation(projectId) {
    try {
      return await this.contract.remainingContributorAllocation(projectId);
    } catch (error) {
      console.error('Error getting remaining contributor allocation:', error);
      throw error;
    }
  }

  /**
   * Get the remaining investor allocation for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<BigNumber>} - Remaining investor allocation
   */
  async getRemainingInvestorAllocation(projectId) {
    try {
      console.log(`Getting remaining investor allocation for project ${projectId}`);
      
      
      // Get the allocation from the contract
      const remaining = await this.contract.remainingInvestorAllocation(projectId);
      console.log(`Contract returned allocation: ${ethers.utils.formatUnits(remaining, 0)} IUs`);
         
      return remaining;
    } catch (error) {
      console.error('Error getting remaining investor allocation:', error);
      throw error;
    }
  }

  /**
   * Get creator information for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Object containing creators and shares
   */
  async getCreatorInfo(projectId) {
    try {
      const result = await this.contract.getCreatorInfo(projectId);
      return {
        creators: result[0],
        shares: result[1].map(share => share.toNumber())
      };
    } catch (error) {
      console.error('Error getting creator info:', error);
      throw error;
    }
  }

  /**
   * Get detailed creator information for a project including actual IU holdings
   * @param {number} projectId - Project ID
   * @returns {Promise<Array>} - Array of objects with creator details including IU balance, role, and share percentages
   */
  async getProjectCreatorsWithHoldings(projectId) {
    try {
      console.log(`Getting detailed creator information for project ${projectId}`);
      
      // First check if the project exists
      const exists = await this.projectExists(projectId);
      if (!exists) {
        console.error(`Project ${projectId} does not exist`);
        return [];
      }
      
      // Get creator info from the contract
      const creatorInfo = await this.getCreatorInfo(projectId);
      if (!creatorInfo || !creatorInfo.creators || creatorInfo.creators.length === 0) {
        console.log(`No creators found for project ${projectId}`);
        return [];
      }
      
      console.log(`Found ${creatorInfo.creators.length} creators for project ${projectId}`);
      
      // Get project data for total supply information
      let projectData = null;
      try {
        projectData = await this.getProjectData(projectId);
      } catch (dataError) {
        console.log(`Could not get project data for project ${projectId}:`, dataError);
      }
      
      // Get total supply for percentage calculation
      const totalSupply = projectData ? projectData.totalSupply : ethers.constants.Zero;
      
      // For each creator, get their IU balance
      const creatorsWithHoldings = await Promise.all(
        creatorInfo.creators.map(async (address, index) => {
          try {
            // Get creator's share percentage
            const creatorShare = creatorInfo.shares[index];
            
            // Get the actual IU balance this creator holds
            const balance = await this.balanceOf(address, projectId);
            const formattedBalance = ethers.utils.formatUnits(balance, 0);
            
            // Calculate percentage of total supply based on actual holdings
            const percentageOfTotal = totalSupply.gt(0)
              ? balance.mul(10000).div(totalSupply).toNumber() / 100
              : 0;
            
            return {
              walletAddress: address,
              iuAllocation: parseInt(formattedBalance),
              balance, // Raw BigNumber for potential sorting
              formattedBalance,
              sharePercentage: creatorShare / 100, // Convert from basis points to percentage
              percentageOfTotal,
              role: 'Creator'
            };
          } catch (error) {
            console.error(`Error getting balance for creator ${address}:`, error);
            // Return with default values in case of error
            return {
              walletAddress: address,
              iuAllocation: 0,
              balance: ethers.constants.Zero,
              formattedBalance: '0',
              sharePercentage: creatorInfo.shares[index] / 100,
              percentageOfTotal: 0,
              role: 'Creator'
            };
          }
        })
      );
      
      // Sort by balance (descending)
      return creatorsWithHoldings.sort((a, b) => (b.balance.gt(a.balance) ? 1 : -1));
      
    } catch (error) {
      console.error(`Error getting creators with holdings for project ${projectId}:`, error);
      return [];
    }
  }

  /**
   * Get contributors information for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Object containing contributors and amounts
   */
  async getContributorsInfo(projectId) {
    try {
      const result = await this.contract.getContributorsInfo(projectId);
      return {
        contributors: result[0],
        amounts: result[1]
      };
    } catch (error) {
      console.error('Error getting contributors info:', error);
      throw error;
    }
  }

  /**
   * Get specific contributor information
   * @param {number} projectId - Project ID
   * @param {string} contributor - Contributor address
   * @returns {Promise<BigNumber>} - Amount of IUs the contributor has
   */
  async getContributorInfo(projectId, contributor) {
    try {
      return await this.contract.getContributorInfo(projectId, contributor);
    } catch (error) {
      console.error('Error getting contributor info:', error);
      throw error;
    }
  }

  /**
   * Get investors information for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Object containing investors and amounts
   */
  async getInvestorsInfo(projectId) {
    try {
      const result = await this.contract.getInvestorsInfo(projectId);
      return {
        investors: result[0],
        amounts: result[1]
      };
    } catch (error) {
      console.error('Error getting investors info:', error);
      throw error;
    }
  }

  /**
   * Get specific investor information
   * @param {number} projectId - Project ID
   * @param {string} investor - Investor address
   * @returns {Promise<BigNumber>} - Amount of IUs the investor has
   */
  async getInvestorInfo(projectId, investor) {
    try {
      return await this.contract.getInvestorInfo(projectId, investor);
    } catch (error) {
      console.error('Error getting investor info:', error);
      throw error;
    }
  }

  /**
   * Format project data for display
   * @param {Object} projectData - Raw project data from the contract
   * @returns {Object} - Formatted project data
   */
  formatProjectData(projectData) {
    if (!projectData) return null;
    
    return {
      totalSupply: ethers.utils.formatEther(projectData.totalSupply),
      initialPrice: ethers.utils.formatEther(projectData.initialPrice),
      creatorsAllocation: projectData.creatorsAllocatedPercentage.toNumber() / 100,
      contributorsAllocation: projectData.contributorsReservePercentage.toNumber() / 100,
      investorsAllocation: projectData.investorsReservePercentage.toNumber() / 100,
      treasuryBalance: ethers.utils.formatEther(projectData.treasuryBalance),
      name: projectData.name
    };
  }

  /**
   * Get the project treasury balance 
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Treasury balance in CEL tokens with formatted value
   */
  async getProjectTreasuryBalance(projectId) {
    try {
      console.log(`Getting treasury balance for project ${projectId}`);
      
      // First check if the project exists
      const exists = await this.projectExists(projectId);
      if (!exists) {
        console.error(`Project ${projectId} does not exist`);
        return {
          raw: ethers.constants.Zero,
          formatted: '0'
        };
      }
      
      // Get the project data which includes treasury balance
      const projectData = await this.getProjectData(projectId);
      console.log(`Project treasury balance: ${ethers.utils.formatEther(projectData.treasuryBalance)} CEL`);
      
      return {
        raw: projectData.treasuryBalance,
        formatted: ethers.utils.formatEther(projectData.treasuryBalance)
      };
    } catch (error) {
      console.error('Error getting project treasury balance:', error);
      return {
        raw: ethers.constants.Zero,
        formatted: '0'
      };
    }
  }

  /**
   * Get all projects where a user is a creator
   * @param {string} userAddress - User's wallet address
   * @param {number} [maxProjects=20] - Maximum number of projects to check
   * @returns {Promise<Array>} - Array of objects with project info and creator status
   */
  async getUserCreatedProjects(userAddress, maxProjects = 20) {
    try {
      console.log(`Getting projects where user ${userAddress} is a creator`);
      
      // Get the total number of projects
      const totalProjects = await this.getTotalProjects();
      console.log(`Total projects in the system: ${totalProjects}`);
      
      // Determine how many projects to check (limit to maxProjects or total)
      const numProjectsToCheck = Math.min(totalProjects, maxProjects);
      
      // Check creator info for all projects in parallel
      const createdProjects = await Promise.all(
        Array.from({ length: numProjectsToCheck }, (_, i) => i + 1).map(async (projectId) => {
          try {
            // Check if project exists
            const exists = await this.projectExists(projectId);
            if (!exists) {
              return null;
            }
            
            // Get creator info
            const creatorInfo = await this.getCreatorInfo(projectId);
            
            // Check if user is one of the creators
            const isCreator = creatorInfo.creators.some(
              creator => creator.toLowerCase() === userAddress.toLowerCase()
            );
            
            if (!isCreator) {
              return null;
            }
            
            // Get project name
            let projectName = `Project #${projectId}`;
            try {
              projectName = await this.getProjectName(projectId);
            } catch (nameError) {
              console.log(`Could not get project name for project ${projectId}:`, nameError);
            }
            
            // Get project data for more details
            let projectData = null;
            try {
              projectData = await this.getProjectData(projectId);
            } catch (dataError) {
              console.log(`Could not get project data for project ${projectId}:`, dataError);
            }
            
            // Get IU balance for this project
            const balance = await this.balanceOf(userAddress, projectId);
            
            // Get total supply for percentage calculation
            const totalSupply = projectData ? projectData.totalSupply : ethers.constants.Zero;
            const percentage = totalSupply.gt(0) 
              ? balance.mul(10000).div(totalSupply).toNumber() / 100
              : 0;
            
            // Find the creator's share percentage
            const creatorIndex = creatorInfo.creators.findIndex(
              creator => creator.toLowerCase() === userAddress.toLowerCase()
            );
            const creatorShare = creatorIndex !== -1 ? creatorInfo.shares[creatorIndex] : 0;
            
            return {
              projectId,
              projectName,
              balance,
              formattedBalance: ethers.utils.formatUnits(balance, 0),
              totalSupply: projectData ? ethers.utils.formatUnits(totalSupply, 0) : '0',
              percentage,
              creatorShare: creatorShare / 100, // Convert from basis points to percentage
              role: 'creator'
            };
          } catch (error) {
            console.error(`Error checking creator status for project ${projectId}:`, error);
            return null;
          }
        })
      );
      
      // Filter out null values and sort by balance (descending)
      const validProjects = createdProjects
        .filter(project => project !== null)
        .sort((a, b) => (b.balance.gt(a.balance) ? 1 : -1));
      
      console.log(`Found ${validProjects.length} projects where user ${userAddress} is a creator`);
      return validProjects;
    } catch (error) {
      console.error('Error getting user created projects:', error);
      return [];
    }
  }

  /**
   * Get all projects where a user is a contributor
   * @param {string} userAddress - User's wallet address
   * @param {number} [maxProjects=20] - Maximum number of projects to check
   * @returns {Promise<Array>} - Array of objects with project info and contributor status
   */
  async getUserContributedProjects(userAddress, maxProjects = 20) {
    try {
      console.log(`Getting projects where user ${userAddress} is a contributor`);
      
      // Get the total number of projects
      const totalProjects = await this.getTotalProjects();
      console.log(`Total projects in the system: ${totalProjects}`);
      
      // Determine how many projects to check (limit to maxProjects or total)
      const numProjectsToCheck = Math.min(totalProjects, maxProjects);
      
      // Check contributor info for all projects in parallel
      const contributedProjects = await Promise.all(
        Array.from({ length: numProjectsToCheck }, (_, i) => i + 1).map(async (projectId) => {
          try {
            // Check if project exists
            const exists = await this.projectExists(projectId);
            if (!exists) {
              return null;
            }
            
            // Get contributor info
            const contributorAmount = await this.getContributorInfo(projectId, userAddress);
            
            // If user has 0 contributor units, skip this project
            if (contributorAmount.isZero()) {
              return null;
            }
            
            // Get project name
            let projectName = `Project #${projectId}`;
            try {
              projectName = await this.getProjectName(projectId);
            } catch (nameError) {
              console.log(`Could not get project name for project ${projectId}:`, nameError);
            }
            
            // Get project data for more details
            let projectData = null;
            try {
              projectData = await this.getProjectData(projectId);
            } catch (dataError) {
              console.log(`Could not get project data for project ${projectId}:`, dataError);
            }
            
            // Get total supply for percentage calculation
            const totalSupply = projectData ? projectData.totalSupply : ethers.constants.Zero;
            const percentage = totalSupply.gt(0) 
              ? contributorAmount.mul(10000).div(totalSupply).toNumber() / 100
              : 0;
            
            return {
              projectId,
              projectName,
              balance: contributorAmount,
              formattedBalance: ethers.utils.formatUnits(contributorAmount, 0),
              totalSupply: projectData ? ethers.utils.formatUnits(totalSupply, 0) : '0',
              percentage,
              role: 'contributor'
            };
          } catch (error) {
            console.error(`Error checking contributor status for project ${projectId}:`, error);
            return null;
          }
        })
      );
      
      // Filter out null values and sort by balance (descending)
      const validProjects = contributedProjects
        .filter(project => project !== null)
        .sort((a, b) => (b.balance.gt(a.balance) ? 1 : -1));
      
      console.log(`Found ${validProjects.length} projects where user ${userAddress} is a contributor`);
      return validProjects;
    } catch (error) {
      console.error('Error getting user contributed projects:', error);
      return [];
    }
  }

  /**
   * Get all projects where a user is an investor
   * @param {string} userAddress - User's wallet address
   * @param {number} [maxProjects=20] - Maximum number of projects to check
   * @returns {Promise<Array>} - Array of objects with project info and investor status
   */
  async getUserInvestedProjects(userAddress, maxProjects = 20) {
    try {
      console.log(`Getting projects where user ${userAddress} is an investor`);
      
      // Get the total number of projects
      const totalProjects = await this.getTotalProjects();
      console.log(`Total projects in the system: ${totalProjects}`);
      
      // Determine how many projects to check (limit to maxProjects or total)
      const numProjectsToCheck = Math.min(totalProjects, maxProjects);
      
      // Check investor info for all projects in parallel
      const investedProjects = await Promise.all(
        Array.from({ length: numProjectsToCheck }, (_, i) => i + 1).map(async (projectId) => {
          try {
            // Check if project exists
            const exists = await this.projectExists(projectId);
            if (!exists) {
              return null;
            }
            
            // Get investor info
            const investorAmount = await this.getInvestorInfo(projectId, userAddress);
            
            // If user has 0 investor units, skip this project
            if (investorAmount.isZero()) {
              return null;
            }
            
            // Get project name
            let projectName = `Project #${projectId}`;
            try {
              projectName = await this.getProjectName(projectId);
            } catch (nameError) {
              console.log(`Could not get project name for project ${projectId}:`, nameError);
            }
            
            // Get project data for more details
            let projectData = null;
            try {
              projectData = await this.getProjectData(projectId);
            } catch (dataError) {
              console.log(`Could not get project data for project ${projectId}:`, dataError);
            }
            
            // Get total supply for percentage calculation
            const totalSupply = projectData ? projectData.totalSupply : ethers.constants.Zero;
            const percentage = totalSupply.gt(0) 
              ? investorAmount.mul(10000).div(totalSupply).toNumber() / 100
              : 0;
            
            return {
              projectId,
              projectName,
              balance: investorAmount,
              formattedBalance: ethers.utils.formatUnits(investorAmount, 0),
              totalSupply: projectData ? ethers.utils.formatUnits(totalSupply, 0) : '0',
              percentage,
              role: 'investor'
            };
          } catch (error) {
            console.error(`Error checking investor status for project ${projectId}:`, error);
            return null;
          }
        })
      );
      
      // Filter out null values and sort by balance (descending)
      const validProjects = investedProjects
        .filter(project => project !== null)
        .sort((a, b) => (b.balance.gt(a.balance) ? 1 : -1));
      
      console.log(`Found ${validProjects.length} projects where user ${userAddress} is an investor`);
      return validProjects;
    } catch (error) {
      console.error('Error getting user invested projects:', error);
      return [];
    }
  }

  /**
   * Get all projects where a user holds Innovation Units with role identification
   * @param {string} userAddress - User's wallet address
   * @param {number} [maxProjects=20] - Maximum number of projects to check
   * @returns {Promise<Array>} - Array of objects with project info, IU balance and role
   */
  async getAllUserIUHoldings(userAddress, maxProjects = 20) {
    try {
      console.log(`Getting all IU holdings for user ${userAddress} with role identification`);
      
      // First, get all the projects with IU balance
      const [creatorProjects, contributorProjects, investorProjects] = await Promise.all([
        this.getUserCreatedProjects(userAddress, maxProjects),
        this.getUserContributedProjects(userAddress, maxProjects),
        this.getUserInvestedProjects(userAddress, maxProjects)
      ]);
      
      // Combine all projects
      const allProjects = [
        ...creatorProjects,
        ...contributorProjects,
        ...investorProjects
      ];
      
      // Get the total number of projects for general IU balance check
      const totalProjects = await this.getTotalProjects();
      const numProjectsToCheck = Math.min(totalProjects, maxProjects);
      
      // Check for any general IU holdings not covered by specific roles
      const allHoldings = await Promise.all(
        Array.from({ length: numProjectsToCheck }, (_, i) => i + 1).map(async (projectId) => {
          try {
            // Skip if we already have this project in our list
            if (allProjects.some(p => p.projectId === projectId)) {
              return null;
            }
            
            // Check if project exists
            const exists = await this.projectExists(projectId);
            if (!exists) {
              return null;
            }
            
            // Get IU balance
            const balance = await this.balanceOf(userAddress, projectId);
            
            // If balance is 0, skip this project
            if (balance.isZero()) {
              return null;
            }
            
            // Get project name
            let projectName = `Project #${projectId}`;
            try {
              projectName = await this.getProjectName(projectId);
            } catch (nameError) {
              console.log(`Could not get project name for project ${projectId}:`, nameError);
            }
            
            // Get project data for more details
            let projectData = null;
            try {
              projectData = await this.getProjectData(projectId);
            } catch (dataError) {
              console.log(`Could not get project data for project ${projectId}:`, dataError);
            }
            
            // Get total supply for percentage calculation
            const totalSupply = projectData ? projectData.totalSupply : ethers.constants.Zero;
            const percentage = totalSupply.gt(0) 
              ? balance.mul(10000).div(totalSupply).toNumber() / 100
              : 0;
            
            return {
              projectId,
              projectName,
              balance,
              formattedBalance: ethers.utils.formatUnits(balance, 0),
              totalSupply: projectData ? ethers.utils.formatUnits(totalSupply, 0) : '0',
              percentage,
              role: 'holder' // Generic holder for IUs that don't match specific roles
            };
          } catch (error) {
            console.error(`Error checking IU balance for project ${projectId}:`, error);
            return null;
          }
        })
      );
      
      // Combine all projects and holdings
      const allHoldingsWithRoles = [
        ...allProjects,
        ...allHoldings.filter(holding => holding !== null)
      ].sort((a, b) => (b.balance.gt(a.balance) ? 1 : -1));
      
      console.log(`Found ${allHoldingsWithRoles.length} total projects with IU holdings for user ${userAddress}`);
      return allHoldingsWithRoles;
    } catch (error) {
      console.error('Error getting all user IU holdings with roles:', error);
      return [];
    }
  }

  /**
   * Get the total circulating supply of IUs for a specific project
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Total supply in raw and formatted values
   */
  async getCirculatingSupply(projectId) {
    try {
      console.log(`Getting circulating supply for project ${projectId}`);
      
      // First check if the project exists
      const exists = await this.projectExists(projectId);
      if (!exists) {
        console.error(`Project ${projectId} does not exist`);
        return {
          raw: ethers.constants.Zero,
          formatted: '0'
        };
      }
      
      // Get the total supply from the contract
      const totalSupply = await this.contract.getTotalSupply(projectId);
      console.log(`Project circulating supply: ${ethers.utils.formatUnits(totalSupply, 0)} IUs`);
      
      return {
        raw: totalSupply,
        formatted: ethers.utils.formatUnits(totalSupply, 0)
      };
    } catch (error) {
      console.error('Error getting project circulating supply:', error);
      return {
        raw: ethers.constants.Zero,
        formatted: '0'
      };
    }
  }

  /**
   * Get the total IUs held by an innovator across all projects
   * @param {string} walletAddress - Innovator's wallet address
   * @param {number} [maxProjects=50] - Maximum number of projects to check
   * @returns {Promise<Object>} - Total IUs in raw and formatted values
   */
  async getInnovatorTotalIUs(walletAddress, maxProjects = 50) {
    try {
      console.log(`Getting total IU holdings for innovator ${walletAddress}`);
      
      // Get the total number of projects
      const totalProjects = await this.getTotalProjects();
      console.log(`Total projects in the system: ${totalProjects}`);
      
      // Determine how many projects to check (limit to maxProjects or total)
      const numProjectsToCheck = Math.min(totalProjects, maxProjects);
      
      // Check IU balance for all projects in parallel
      let totalBalance = ethers.BigNumber.from('0');
      
      const balances = await Promise.all(
        Array.from({ length: numProjectsToCheck }, (_, i) => i).map(async (projectId) => {
          try {
            // Check if project exists
            const exists = await this.projectExists(projectId);
            if (!exists) {
              return ethers.BigNumber.from('0');
            }
            
            // Get IU balance for this project
            const balance = await this.balanceOf(walletAddress, projectId);
            console.log(`Project ${projectId} balance: ${ethers.utils.formatUnits(balance, 0)} IUs`);
            
            return balance;
          } catch (error) {
            console.error(`Error checking IU balance for project ${projectId}:`, error);
            return ethers.BigNumber.from('0');
          }
        })
      );
      
      // Sum up all balances
      totalBalance = balances.reduce((sum, balance) => sum.add(balance), ethers.BigNumber.from('0'));
      
      console.log(`Total IU holdings for ${walletAddress}: ${ethers.utils.formatUnits(totalBalance, 0)} IUs`);
      
      return {
        raw: totalBalance,
        formatted: ethers.utils.formatUnits(totalBalance, 0)
      };
    } catch (error) {
      console.error('Error getting innovator total IU holdings:', error);
      return {
        raw: ethers.BigNumber.from('0'),
        formatted: '0'
      };
    }
  }

  /**
   * Get the hardcoded global innovation units total supply value 
   * This is the default total supply used for all new projects
   * @returns {Promise<Object>} - Total supply in raw and formatted values
   */
  async getInnovationUnitsTotalSupply() {
    try {
      // Get the total supply from the contract
      const totalSupply = await this.contract.getInnovationUnitsTotalSupply();
      console.log(`Global innovation units total supply: ${ethers.utils.formatUnits(totalSupply, 0)}`);
      
      return {
        raw: totalSupply,
        formatted: ethers.utils.formatUnits(totalSupply, 0)
      };
    } catch (error) {
      console.error('Error getting global innovation units total supply:', error);
      return {
        raw: ethers.constants.Zero,
        formatted: '0'
      };
    }
  }

  /**
   * Get the buy fee percentage
   * @returns {Promise<Object>} - Fee percentage in raw basis points and formatted percentage
   */
  async getBuyFeePercentage() {
    try {
      const buyFeeInBasisPoints = await this.contract.buyFeePercentage();
      // Convert basis points (1/100 of a percent) to percentage
      // 100 basis points = 1%
      const formattedPercentage = buyFeeInBasisPoints.toNumber() / 100;
      
      return {
        raw: buyFeeInBasisPoints,
        formatted: formattedPercentage.toFixed(2)
      };
    } catch (error) {
      console.error('Error getting buy fee percentage:', error);
      return {
        raw: 0,
        formatted: '0.00'
      };
    }
  }
}

export default InnovationUnitsService; 